import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RootState } from "../../app/store";
import { useAppSelector, useAppDispatch } from "../../app/hooks";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
} from "@progress/kendo-react-grid";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { Typography } from "@progress/kendo-react-common";
import { Button } from "@progress/kendo-react-buttons";
import {
  HeaderThElement,
  PagerTargetEvent,
} from "@progress/kendo-react-data-tools";
import { LoadingPanel } from "../../components/layout/Loading";
import {
  filterBy,
  CompositeFilterDescriptor,
} from "@progress/kendo-data-query";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import IconButton from "../../components/common/IconButton";
import { FiEdit } from "react-icons/fi";
import { MdDelete } from "react-icons/md";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import { Switch } from "@progress/kendo-react-inputs";
import AlertBox from "../../components/common/AlertBox";
import { Tooltip } from "@progress/kendo-react-tooltip";
import {
  ActiveInactiveHotel,
  bulkUploadHotel,
  deleteHotel,
  getAllHotels,
} from "./services/hotel.services";
import { clearHotelID, setHotelID } from "./hotelSlice";
import { checkAcessRights } from "../../_helper/helper";
import {
  DELETE_ACCESS,
  EDIT_ACCESS,
  INSERT_ACCESS,
} from "../../_contstants/common";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { Field, Form, FormElement } from "@progress/kendo-react-form";
import { RxCross1 } from "react-icons/rx";
import FormFileUpload from "../../components/formFields/FormFileUpload";
import hotelSample from "../../assets/Excels/hotel_sample.xlsx";

const Hotel: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isAddAccess = checkAcessRights(location.pathname, INSERT_ACCESS);

  useEffect(() => {
    dispatch(getAllHotels({ HotelName: "" }));
    dispatch(closeDialog());
  }, []);

  const handleHotelCreate = () => {
    navigate("/hotel/create");
  };

  const handleOpenBulkUploadDialog = () => {
    dispatch(openDialog("uploadHotelData"));
  };

  return (
    <>
      <DeleteHotelDialog />
      <UploadHotelDataDialog />
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 0px 20px",
          }}
        >
          <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
            Hotel List
          </Typography.h4>
          <div>
            {isAddAccess && (
              <Button
                themeColor={"primary"}
                fillMode={"solid"}
                style={{ marginRight: 10 }}
                onClick={() => handleOpenBulkUploadDialog()}
              >
                Bulk Upload
              </Button>
            )}
            {isAddAccess && (
              <Button
                onClick={handleHotelCreate}
                fillMode={"solid"}
                themeColor={"primary"}
              >
                Add New
              </Button>
            )}
          </div>
        </GridLayoutItem>
        <GridLayoutItem>
          <HotelGridComponent />
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

interface PageState {
  skip: number;
  take: number;
}

const initialDataState: PageState = { skip: 0, take: 10 };

const HotelGridComponent: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isEditAccess = checkAcessRights(location.pathname, EDIT_ACCESS);
  const isDeleteAccess = checkAcessRights(location.pathname, DELETE_ACCESS);

  const HotelList = useAppSelector((state: RootState) => state.hotel.HotelList);
  const loading = useAppSelector((state: RootState) => state.hotel.loading);

  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);
  const [page, setPage] = React.useState<PageState>(initialDataState);
  const [pageSizeValue, setPageSizeValue] = React.useState<
    number | string | undefined
  >();
  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take =
      targetEvent.value === "All" ? HotelList.length : event.page.take;

    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setPage({
      ...event.page,
      take,
    });
  };

  const MyEditCommandCell = (props: GridCellProps) => {
    const handleEditHotel = (ID: number) => {
      navigate("/hotel/edit", { state: { HotelID: ID } });
    };
    const handleOpenDeleteDialog = (ID: number) => {
      dispatch(openDialog("deleteHotel"));
      dispatch(setHotelID(ID));
    };
    return (
      <td
        className={`action-td ${props.className}`}
        style={{ ...props.style, right: 0 }}
      >
        {isEditAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Edit"
            onClick={() => handleEditHotel(props.dataItem.ID)}
            icon={FiEdit}
            iconClassName="absolute-position"
            iconStyle={{ fontSize: "20px" }}
          />
        )}
        {isDeleteAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Delete"
            className="color-red"
            onClick={() => handleOpenDeleteDialog(props.dataItem.ID)}
            icon={MdDelete}
            iconClassName="absolute-position"
            iconStyle={{ fontSize: "24px" }}
          />
        )}
      </td>
    );
  };
  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
    setPage({ skip: 0, take: page.take });
  };

  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className={`table-header ${
        props.index === 5 || props.index === 6 ? "locked-header" : ""
      }`}
    >
      {props.children}
    </HeaderThElement>
  );
  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };
  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );
  const MyActiveInActtiveCommandCell = (props: GridCellProps) => {
    const [IsActive, setIsActive] = useState<boolean>(props.dataItem.IsActive);

    const handleActiveInActive = () => {
      setIsActive(!IsActive);
      const data = {
        ID: props.dataItem.ID,
        IsActive: !IsActive,
      };
      dispatch(ActiveInactiveHotel(data)).then((response: any) => {
        if (response.payload?.data?.Status === 200) {
          dispatch(getAllHotels({ HotelName: "" }));
        } else {
          dispatch(getAllHotels({ HotelName: "" }));
        }
      });
    };

    return (
      <td
        style={{ textAlign: "center", ...props.style, right: 110 }}
        className={props.className}
      >
        <Tooltip anchorElement="target" position="top" parentTitle={true}>
          <div title={`Set ${IsActive ? "Inactive" : "Active"}.`}>
            <Switch
              disabled={isEditAccess ? false : true}
              name="IsActive"
              checked={IsActive}
              onChange={handleActiveInActive}
              size="small"
            />
          </div>
        </Tooltip>
      </td>
    );
  };

  const gridRef = useRef<any>(null);
  if (loading) return <LoadingPanel gridRef={gridRef} />;

  return (
    <>
      {HotelList && HotelList.length ? (
        <KendoGrid
          style={{ maxHeight: "calc(100vh - 152px)" }}
          className="responsive-table"
          filterable={true}
          filter={filter}
          data={
            filter
              ? filterBy(HotelList, filter).slice(
                  page.skip,
                  page.take + page.skip
                )
              : HotelList.slice(page.skip, page.take + page.skip)
          }
          onFilterChange={handleFilterChange}
          skip={page.skip}
          take={page.take}
          total={HotelList.length}
          pageable={{
            buttonCount: 5,
            pageSizes: [5, 10, 15, "All"],
            pageSizeValue: pageSizeValue,
            type: "input",
          }}
          onPageChange={pageChange}
          cells={{
            headerCell: HeaderCustomCell,
            data: MyDataCustomCell,
          }}
        >
          <Column
            field="HotelName"
            title="Hotel"
            width={200}
            cell={(props: any) => (
              <td>
                {isEditAccess ? (
                  <div
                    className="hoverableName"
                    onClick={() =>
                      navigate("/hotel/edit", {
                        state: {
                          HotelID: props.dataItem?.ID,
                        },
                      })
                    }
                  >
                    {props.dataItem?.HotelName}
                  </div>
                ) : (
                  <div className="normalName">{props.dataItem?.HotelName}</div>
                )}
              </td>
            )}
          />
          <Column field="Address" title="Address" width={300} />
          <Column field="CityName" title="City" width={200} />
          <Column field="StateName" title="State" width={200} />
          <Column field="CountryName" title="Country" width={200} />
          <Column
            field="ID"
            title="Status"
            cell={MyActiveInActtiveCommandCell}
            width={"90px"}
            filterable={false}
            locked={true}
          />
          {(isEditAccess || isDeleteAccess) && (
            <Column
              field="ID"
              title="Actions"
              cell={MyEditCommandCell}
              width={"110px"}
              filterable={false}
              locked={true}
            />
          )}
        </KendoGrid>
      ) : (
        <AlertBox />
      )}
    </>
  );
};

const DeleteHotelDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const dialogName = useAppSelector(
    (state: RootState) => state.dialog.dialogName
  );
  const HotelID = useAppSelector((state: RootState) => state.hotel.HotelID);
  const handleDeleteAction = async (ID: number | null) => {
    if (ID) {
      const response = await dispatch(deleteHotel(ID));
      if (response.meta.requestStatus === "fulfilled") {
        dispatch(getAllHotels({ HotelName: "" }));
        dispatch(closeDialog());
        dispatch(clearHotelID());
      } else {
        dispatch(getAllHotels({ HotelName: "" }));
        dispatch(closeDialog());
        dispatch(clearHotelID());
      }
    }
  };
  return (
    <>
      {dialogName === "deleteHotel" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete Hotel?"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this Hotel?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => handleDeleteAction(HotelID)}
                  type="button"
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

const UploadHotelDataDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const loading = useAppSelector((state) => state.hotel.hotelUploadLoading);

  const handleSubmit = async (values: any) => {
    const formData = new FormData();
    formData.append("file", values?.HotelDocument[0]?.getRawFile());

    dispatch(bulkUploadHotel(formData)).then((res: any) => {
      if (res?.payload?.Status === 200) {
        dispatch(closeDialog());
        dispatch(getAllHotels({ HotelName: "" }));
      }
    });
  };

  return (
    <>
      {dialogName === "uploadHotelData" && (
        <AppDialog>
          <>
            {loading && <LoadingPanel gridRef={gridRef} />}
            <Form
              onSubmit={handleSubmit}
              render={() => (
                <FormElement style={{ minWidth: 500 }}>
                  <GridLayout>
                    <GridLayoutItem className="position-relative">
                      <Typography.h4 className="typography-head">
                        {"Upload Hotel Data"}
                      </Typography.h4>
                      <IconButton
                        className="typography-dialog-close-icon"
                        type="button"
                        fillMode={"flat"}
                        themeColor={"dark"}
                        style={{ marginRight: 4, top: 7 }}
                        onClick={() => dispatch(closeDialog())}
                        icon={RxCross1}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        name="HotelDocument"
                        type="file"
                        filetype="file"
                        label="Hotel File"
                        accept=".xls, .xlsx, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        multiple={false}
                        component={FormFileUpload}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginTop: 20,
                      }}
                    >
                      <Button
                        themeColor={"primary"}
                        fillMode={"solid"}
                        style={{ marginRight: 10 }}
                        onClick={() => window.open(hotelSample)}
                      >
                        Show Sample
                      </Button>
                      <div>
                        <DialogCloseButton themeColor="primary" />
                        <ButtonWithLoading
                          label={"Upload"}
                          type="submit"
                          // disabled={!formRenderProps.allowSubmit || formLoading}
                          // loading={formLoading}
                        />
                      </div>
                    </GridLayoutItem>
                  </GridLayout>
                </FormElement>
              )}
            />
          </>
        </AppDialog>
      )}
    </>
  );
};

export default Hotel;
